import React, { useEffect, useRef, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

/**
 * Aviso sonoro de mensagem privada no chat da equipe.
 *
 * Combina duas decisões: a da clínica (`clinic_settings`, vale para a unidade)
 * e a do usuário (preferência local, para quem atende em recepção silenciosa).
 * O som só toca quando as duas permitem.
 */

const USER_PREFERENCE_KEY = 'klinvia:teamChatSoundEnabled';
const USER_VOLUME_KEY = 'klinvia:teamChatSoundVolume';
const DEFAULT_SOUND_VOLUME = 1.5;
const MIN_SOUND_VOLUME = 0.5;
const MAX_SOUND_VOLUME = 2.5;
const SETTINGS_UPDATED_EVENT = 'clinic-settings:updated';
const USER_PREFERENCE_EVENT = 'team-chat-sound:user-preference';
const USER_VOLUME_EVENT = 'team-chat-sound:user-volume';

let cachedClinicFlag: boolean | undefined;
let inflight: Promise<boolean> | null = null;

async function fetchClinicFlag(): Promise<boolean> {
  if (cachedClinicFlag !== undefined) return cachedClinicFlag;
  if (!inflight) {
    inflight = axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        // Ausente = ligado: a coluna nasce com DEFAULT TRUE.
        cachedClinicFlag = res.data?.data?.team_chat_direct_sound_enabled !== false;
        return cachedClinicFlag;
      })
      .catch(() => {
        cachedClinicFlag = false;
        return false;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function invalidateTeamChatSoundCache(): void {
  cachedClinicFlag = undefined;
  inflight = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED_EVENT));
  }
}

export function readTeamChatSoundUserPreference(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(USER_PREFERENCE_KEY) !== 'off';
}

export function setTeamChatSoundUserPreference(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(USER_PREFERENCE_KEY, enabled ? 'on' : 'off');
  window.dispatchEvent(new CustomEvent(USER_PREFERENCE_EVENT, { detail: { enabled } }));
}

export function readTeamChatSoundVolume(): number {
  if (typeof window === 'undefined') return DEFAULT_SOUND_VOLUME;
  const raw = window.localStorage.getItem(USER_VOLUME_KEY);
  if (raw == null) return DEFAULT_SOUND_VOLUME;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) return DEFAULT_SOUND_VOLUME;
  return Math.min(MAX_SOUND_VOLUME, Math.max(MIN_SOUND_VOLUME, parsed));
}

export function setTeamChatSoundVolume(volume: number): void {
  if (typeof window === 'undefined') return;
  const clamped = Math.min(MAX_SOUND_VOLUME, Math.max(MIN_SOUND_VOLUME, volume));
  window.localStorage.setItem(USER_VOLUME_KEY, String(clamped));
  window.dispatchEvent(new CustomEvent(USER_VOLUME_EVENT, { detail: { volume: clamped } }));
}

export function useTeamChatSoundEnabled(): {
  /** Resultado final: clínica permite E usuário permite. */
  enabled: boolean;
  clinicEnabled: boolean;
  userEnabled: boolean;
  volume: number;
  /** Ref sempre atualizada — para uso dentro de listeners de socket. */
  enabledRef: React.MutableRefObject<boolean>;
  volumeRef: React.MutableRefObject<number>;
  setUserEnabled: (value: boolean) => void;
  setVolume: (value: number) => void;
} {
  const [clinicEnabled, setClinicEnabled] = useState<boolean>(cachedClinicFlag ?? false);
  const [userEnabled, setUserEnabledState] = useState<boolean>(readTeamChatSoundUserPreference);
  const [volume, setVolumeState] = useState<number>(readTeamChatSoundVolume);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      fetchClinicFlag().then((value) => {
        if (!cancelled) setClinicEnabled(value);
      });
    };

    load();

    const handleSettingsUpdated = () => {
      cachedClinicFlag = undefined;
      load();
    };
    const handleUserPreference = () => setUserEnabledState(readTeamChatSoundUserPreference());
    const handleUserVolume = () => setVolumeState(readTeamChatSoundVolume());

    window.addEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    window.addEventListener(USER_PREFERENCE_EVENT, handleUserPreference);
    window.addEventListener(USER_VOLUME_EVENT, handleUserVolume);
    // Trocar de unidade muda a linha de clinic_settings vigente.
    window.addEventListener('unit:changed', handleSettingsUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
      window.removeEventListener(USER_PREFERENCE_EVENT, handleUserPreference);
      window.removeEventListener(USER_VOLUME_EVENT, handleUserVolume);
      window.removeEventListener('unit:changed', handleSettingsUpdated);
    };
  }, []);

  const enabled = clinicEnabled && userEnabled;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  return {
    enabled,
    clinicEnabled,
    userEnabled,
    volume,
    enabledRef,
    volumeRef,
    setUserEnabled: (value: boolean) => {
      setTeamChatSoundUserPreference(value);
      setUserEnabledState(value);
    },
    setVolume: (value: number) => {
      setTeamChatSoundVolume(value);
      setVolumeState(readTeamChatSoundVolume());
    },
  };
}

export default useTeamChatSoundEnabled;
